import { Divider, Stack, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import ProductCatalogFilterCard from './ProductCatalogFilterCard';
import type ViewProductAttributeTypeStore from '../../AppData/_stores/ViewProductAttributeTypeStore';
import type ViewProductAttributeValueStore from '../../AppData/_stores/ViewProductAttributeValueStore';
import { useAppSelector } from '../../Common/_hooks/useAppSelector';
import useProductCatalogActions from '../hooks/useProductCatalogActions';
import useProductCatalogFiltering from '../hooks/useProductCatalogFiltering';

export default function ProductCatalogFilters() {
  const viewProducts = useAppSelector(state => state.get('appData').get('viewProducts'));
  const viewProductAttributeTypes = useAppSelector(state => state.get('appData').get('viewProductAttributeTypes'));
  const viewProductAttributeValues = useAppSelector(state => state.get('appData').get('viewProductAttributeValues'));
  const {
    isPending,
    toggleAttributeFilter,
  } = useProductCatalogActions();
  const {
    activeFiltersByType,
    matchesProductAttributeFilters,
    matchesProductBase,
  } = useProductCatalogFiltering();

  const valuesByType = useMemo(() => {
    const nextValuesByType = new Map<number, ViewProductAttributeValueStore[]>();

    viewProductAttributeValues.forEach(value => {
      const typeValues = nextValuesByType.get(value.get('type')) ?? [];
      typeValues.push(value);
      nextValuesByType.set(value.get('type'), typeValues);
    });

    return nextValuesByType;
  }, [viewProductAttributeValues])

  const onFilterClick = useCallback((typeId: number, valueId: number) => {
    toggleAttributeFilter(typeId, valueId);
  }, [toggleAttributeFilter])

  const filterValueCounts = useMemo(() => {
    const counts = new Map<number, Map<number, number>>();

    viewProducts.forEach(product => {
      if (!matchesProductBase(product)) {
        return;
      }

      product.get('listAttributes').forEach((valueId, typeIdKey) => {
        const typeId = Number(typeIdKey);

        if (!matchesProductAttributeFilters(product, typeId)) {
          return;
        }

        const typeCounts = counts.get(typeId) ?? new Map<number, number>();
        typeCounts.set(valueId, (typeCounts.get(valueId) ?? 0) + 1);
        counts.set(typeId, typeCounts);
      });
    });

    return counts;
  }, [matchesProductAttributeFilters, matchesProductBase, viewProducts])

  const renderType = useCallback((type: ViewProductAttributeTypeStore) => {
    return <Stack spacing={2} key={type.get('id')}>
      <Typography variant="h4">{type.get('name')}</Typography>
      <Stack spacing={1}>
        {(valuesByType.get(type.get('id')) ?? []).map(value => {
          const filters = activeFiltersByType.get(type.get('id')) ?? new Set<string>();
          const selected = filters.has(String(value.get('id')));
          const count = filterValueCounts.get(type.get('id'))?.get(value.get('id')) ?? 0;
          const disabled = !selected && count === 0;

          return <ProductCatalogFilterCard
            key={value.get('id')}
            count={count}
            disabled={disabled}
            image={value.get('slug')}
            name={value.get('name')}
            selected={selected}
            onChange={() => onFilterClick(type.get('id'), value.get('id'))}
          />
        })}
      </Stack>
    </Stack>
  }, [activeFiltersByType, filterValueCounts, onFilterClick, valuesByType])

  return <Stack
    spacing={2}
    divider={<Divider />}
    sx={{
      opacity: isPending ? 0.72 : 1,
      transition: 'opacity 120ms ease',
    }}
  >
    {viewProductAttributeTypes.valueSeq().map(renderType)}
  </Stack>
}
